import { html, CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { BlocksElement } from '../../BlocksElement';
import styles from '../icon.styles';
import type { IconColorType, IconSize } from './icon.model';

/**
 * Calendar component
 * @csspart base- The component's base wrapper
 */
export class BlocksIconCalendar extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Icon color variant
   * <cat-text-passage size="sm">
   * <ul>
   * <li>No color defined renders an icon with the currentColor of its parent element</li>
   * <li>**content-strong** renders an icon with the cat-theme-color-content-strong color token</li>
   * <li>**knockout** renders an icon with the cat-theme-color-content-knockout color token</li>
   * <li>**knockout-brand** renders a renders an icon with the cat-theme-color-content-knockout-brand color token</li>
   * <li>**information** renders an icon with the cat-theme-color-content-utility-information color token</li>
   * <li>**success** renders an icon with the cat-theme-color-content-utility-success color token</li>
   * <li>**warning** renders a renders an icon with the cat-theme-color-content-utility-warning color token</li>
   * <li>**error** renders an icon with the cat-theme-color-content-utility-error color token</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  color: IconColorType;

  /**
   * Title
   * 1) Icons should always have associated text. Use this to supply `aria-label` to icon if it stands by itself without any visual/visually-hidden text
   */
  @property({ type: String })
  iconTitle: string | undefined;

  /**
   * Size property
   * - Default size is 16px
   * - **sm** renders a larger size than default (12px)
   * - **lg** renders a larger size than default (24px)
   * - **xl** renders a larger size than the lg variant (32px);
   */
  @property({ type: String })
  size: IconSize;

  connectedCallback() {
    super.connectedCallback();
    this.classList.add('cat-icon');
  }

  render() {
    const componentClassName = this.componentClassNames('cat-c-icon', {
      'cat-c-icon--sm': this.size === 'sm',
      'cat-c-icon--lg': this.size === 'lg',
      'cat-c-icon--xl': this.size === 'xl',
      'cat-c-icon--content-strong': this.color === 'content-strong',
      'cat-c-icon--knockout': this.color === 'knockout',
      'cat-c-icon--knockout-brand': this.color === 'knockout-brand',
      'cat-c-icon--information': this.color === 'information',
      'cat-c-icon--success': this.color === 'success',
      'cat-c-icon--warning': this.color === 'warning',
      'cat-c-icon--error': this.color === 'error'
    });

    return html`
      <span part="base" class="${componentClassName}" role="presentation">
        <svg
          aria-hidden="${!this.iconTitle}"
          aria-label="${ifDefined(this.iconTitle)}"
          role="${this.iconTitle && 'img'}"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M6.53784 1.10498C6.95206 1.10498 7.28784 1.44077 7.28784 1.85498V3.4458H16.7131V1.85498C16.7131 1.44077 17.0489 1.10498 17.4631 1.10498C17.8773 1.10498 18.2131 1.44077 18.2131 1.85498V3.4458H20.5848C21.1976 3.4458 21.7855 3.68924 22.2188 4.12265C22.652 4.55593 22.8956 5.14364 22.8956 5.75658V9.65894V20.584C22.8956 21.1968 22.652 21.7847 22.2188 22.2179C21.7855 22.6512 21.1977 22.8948 20.5848 22.8948H3.41625C2.80331 22.8948 2.21561 22.6512 1.78233 22.218C1.3489 21.7846 1.10547 21.1968 1.10547 20.584V9.65894V5.75658C1.10547 5.14371 1.34894 4.55595 1.78229 4.1226C2.21564 3.68927 2.80338 3.4458 3.41625 3.4458H5.78784V1.85498C5.78784 1.44077 6.12363 1.10498 6.53784 1.10498ZM16.7131 4.9458V6.53732C16.7131 6.95153 17.0489 7.28732 17.4631 7.28732C17.8773 7.28732 18.2131 6.95153 18.2131 6.53732V4.9458H20.5848C20.7999 4.9458 21.0061 5.03125 21.1581 5.18323C21.3102 5.33536 21.3956 5.54163 21.3956 5.75658V8.90894H2.60547V5.75658C2.60547 5.54155 2.69088 5.33534 2.84293 5.18328C2.99499 5.03123 3.20122 4.9458 3.41625 4.9458H5.78784V6.53732C5.78784 6.95153 6.12363 7.28732 6.53784 7.28732C6.95206 7.28732 7.28784 6.95153 7.28784 6.53732V4.9458H16.7131ZM2.60547 10.4089H21.3956V20.584C21.3956 20.799 21.3101 21.0053 21.1581 21.1573C21.0061 21.3093 20.7998 21.3948 20.5848 21.3948H3.41625C3.20129 21.3948 2.99502 21.3093 2.84289 21.1572C2.69092 21.0053 2.60547 20.799 2.60547 20.584V10.4089Z"
          />
        </svg>
      </span>
    `;
  }
}

if (customElements.get('cat-icon-calendar') === undefined) {
  customElements.define('cat-icon-calendar', BlocksIconCalendar);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-icon-calendar': BlocksIconCalendar;
  }
}
